// @catalog F.030
// StoreMarginAnalysisTable — estilo handoff con filtro por nombre + sort por columna.
// Tabla de tiendas/vendedores con Tag de margen % + DeltaPill crecimiento.
import { useMemo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import { BusinessCenter, Store } from "@mui/icons-material";
import MarginsService, {
  type StoreMarginMetrics,
} from "../../services/margins.service";
import {
  TableShell,
  HeaderCell,
  BodyRow,
  BodyCell,
  RankNum,
  DeltaPill,
  Tag,
  type TagTone,
} from "../analytics/TableCells";
import {
  SortableHeaderCell,
  TableFilterInput,
  useTableControls,
} from "../analytics/TableControls";

interface StoreMarginAnalysisTableProps {
  stores: StoreMarginMetrics[];
}

function marginTone(pct: number): TagTone {
  if (pct >= 34) return "good";
  if (pct >= 26) return "neutral";
  if (pct >= 18) return "warn";
  return "alert";
}

export function StoreMarginAnalysisTable({
  stores,
}: StoreMarginAnalysisTableProps): JSX.Element {
  const filterAccessor = useMemo(
    () => (s: StoreMarginMetrics) => s.storeName,
    [],
  );
  const sortAccessors = useMemo(
    () => ({
      storeName: (s: StoreMarginMetrics) => s.storeName,
      marginPct: (s: StoreMarginMetrics) =>
        s.realMarginPercentage ?? s.avgMarginPercentage,
      marginAmount: (s: StoreMarginMetrics) => s.totalMarginAmount,
      revenue: (s: StoreMarginMetrics) => s.totalRevenue,
      cost: (s: StoreMarginMetrics) => s.totalCost,
      discounts: (s: StoreMarginMetrics) => s.totalDiscounts,
      commissions: (s: StoreMarginMetrics) => s.totalCommissions ?? 0,
      products: (s: StoreMarginMetrics) => s.totalProducts,
      growth: (s: StoreMarginMetrics) =>
        s.growthPercentage ?? Number.NEGATIVE_INFINITY,
    }),
    [],
  );

  const { filter, setFilter, sort, toggleSort, processedData } =
    useTableControls<StoreMarginMetrics>({
      data: stores ?? [],
      filterAccessor,
      sortAccessors,
      defaultSort: { key: "marginAmount", dir: "desc" },
    });

  if (!stores || stores.length === 0) {
    return (
      <Alert severity="info">
        No hay datos de tiendas disponibles para el período actual.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <TableFilterInput
        value={filter}
        onChange={setFilter}
        placeholder="Buscar tienda / vendedor…"
        helper={
          filter
            ? `${processedData.length} de ${stores.length}`
            : `${stores.length} tiendas/vendedores`
        }
      />
      {processedData.length === 0 ? (
        <Alert severity="info">Sin resultados para "{filter}".</Alert>
      ) : (
        <TableShell maxHeight={600}>
          <thead>
            <tr>
              <HeaderCell width={40} align="right">
                #
              </HeaderCell>
              <SortableHeaderCell
                sortKey="storeName"
                currentSort={sort}
                onSort={toggleSort}
              >
                Tienda / Vendedor
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="marginPct"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen %
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="marginAmount"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Margen $
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="revenue"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Ventas
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="cost"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Costos
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="discounts"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Descuentos
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="commissions"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Comisión
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="products"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Productos
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKey="growth"
                currentSort={sort}
                onSort={toggleSort}
                align="right"
              >
                Crecimiento
              </SortableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {processedData.map((store, index) => {
              const isVendor =
                store.storeType === "vendor" ||
                store.storeName.includes("(Vendedor)") ||
                store.storeName.includes("(Tienda)");
              const realPct =
                store.realMarginPercentage ?? store.avgMarginPercentage;
              const tone = marginTone(realPct);
              return (
                <BodyRow key={store.storeName}>
                  <BodyCell align="right">
                    <RankNum n={index + 1} />
                  </BodyCell>
                  <BodyCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        minWidth: 0,
                      }}
                    >
                      {isVendor ? (
                        <BusinessCenter
                          sx={{ fontSize: 14, color: "var(--ink-3)" }}
                        />
                      ) : (
                        <Store sx={{ fontSize: 14, color: "var(--ink-3)" }} />
                      )}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 500,
                          color: "var(--ink-1)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {store.storeName}
                      </Box>
                    </Box>
                  </BodyCell>
                  <BodyCell align="right">
                    <Tag label={`${realPct.toFixed(1)}%`} tone={tone} />
                  </BodyCell>
                  <BodyCell align="right">
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {MarginsService.formatCurrency(store.totalMarginAmount)}
                    </Box>
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatCurrency(store.totalRevenue)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatCurrency(store.totalCost)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatCurrency(store.totalDiscounts)}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {store.totalCommissions !== undefined &&
                    store.totalCommissions > 0
                      ? MarginsService.formatCurrency(store.totalCommissions)
                      : "—"}
                  </BodyCell>
                  <BodyCell align="right" muted>
                    {MarginsService.formatNumber(store.totalProducts)}
                  </BodyCell>
                  <BodyCell align="right">
                    <DeltaPill delta={store.growthPercentage ?? null} />
                  </BodyCell>
                </BodyRow>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </Stack>
  );
}
